/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2019. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { forkJoin } from 'rxjs';

import { AssetsService } from 'app/shared/services';
import { ProfileService } from '@profile/services';

@Component({
  selector: 'app-clip-filtering-modal',
  templateUrl: './clip-filtering-modal.component.html',
  styleUrls: ['./clip-filtering-modal.component.scss'],
})
export class ClipFilteringModalComponent implements OnInit {
  @Input() title: string;
  @Input() clips = [];
  @Input() visibleProfileClips: any[] = null;
  @Input() user;

  @Output() closeModal: EventEmitter<any> = new EventEmitter();

  viewAll = false;
  visibleClips = [];
  saveInProgress = false;
  allClips = [];
  formChanged = false;
  addList = {};
  removeList = {};
  hiddenClipsHashTable = {};

  constructor(private assetsService: AssetsService, private profileService: ProfileService) {}

  ngOnInit() {
    // clone the clip object so we don't alter what's shown in the background
    this.allClips = this.clips.map((clip) => ({ ...clip, metadata: { ...clip.metadata } }));
    this.showClips();
  }

  close() {
    this.closeModal.emit({ refresh: false });
  }

  save() {
    this.saveInProgress = true;

    let updateRequests = [];
    if (this.visibleProfileClips) {
      const itemsToAdd = Object.keys(this.addList)
        .map((assetId) => parseInt(assetId))
        .filter((assetId) => !isNaN(assetId));
      const itemsToRemove = Object.keys(this.removeList)
        .map((assetId) => parseInt(assetId))
        .filter((assetId) => !isNaN(assetId));

      if (itemsToAdd.length) {
        updateRequests.push(this.profileService.setProfileAssets(this.user.id, itemsToAdd));
      }
      if (itemsToRemove.length) {
        updateRequests.push(this.profileService.removeProfileAssets(this.user.id, itemsToRemove));
      }
    } else {
      const updatedClips = this.allClips.filter((clip) => clip.updated);
      if (!updatedClips.length) {
        this.saveInProgress = false;
        return;
      }
      updateRequests = updatedClips.map((clip) => {
        return this.assetsService.updateAsset(clip);
      });
    }

    forkJoin(updateRequests).subscribe((updateResults) => {
      this.saveInProgress = false;
      this.addList = [];
      this.removeList = [];
      this.closeModal.emit({ refresh: true });
    });
  }

  toggleShowAll($event, showAll) {
    $event.preventDefault();

    this.viewAll = showAll;
    this.addList = [];
    this.removeList = [];
    this.formChanged = true;
    this.showClips();
  }

  toggleHide(newValue, clip) {
    if (this.visibleProfileClips) {
      if (newValue) {
        this.removeList[clip.id] = true;
        delete this.addList[clip.id];
      } else {
        this.addList[clip.id] = true;
        delete this.removeList[clip.id];
      }
    }

    clip.hide = newValue;

    // support hideOnProfile from metadata
    this.allClips = this.allClips.map((c) => {
      if (c.id === clip.id) {
        if (!clip.metadata || !clip.metadata?.hideOnProfile || clip.metadata?.hideOnProfile === true) {
          if (!clip.metadata) {
            clip.metadata = {};
          }
          clip.metadata.hideOnProfile = {};
        }
        return {
          ...c,
          metadata: { ...clip.metadata, hideOnProfile: { [this.user.id]: newValue } },
          updated: true,
        };
      }
      return c;
    });

    this.formChanged = true;
  }

  playVideo(video) {
    return;
  }

  trackByFn(index, item) {
    return item.id;
  }

  private showClips() {
    if (!this.allClips) {
      this.visibleClips = [];
      return;
    }

    if (this.viewAll) {
      this.visibleClips = this.allClips.map((clip) => ({ ...clip, hide: !this.isClipVisible(clip) }));
      return;
    }

    this.visibleClips = this.allClips.filter((clip) => this.isClipVisible(clip));
  }

  private isClipVisible(clip) {
    if (this.visibleProfileClips) {
      return this.visibleProfileClips.some((profileClip) => profileClip.id === clip.id);
    }

    return (
      (!clip.metadata ||
        // if hide on profile not selected
        !clip.metadata.hideOnProfile ||
        // if hide/show is updated
        clip.updated ||
        // if view all is selected
        this.viewAll ||
        // if hidden on profile for athlete
        (clip?.metadata?.hideOnProfile !== true &&
          clip?.metadata?.hideOnProfile &&
          !clip.metadata.hideOnProfile[this.user.id])) &&
      !clip.private
    );
  }
}
